import React from 'react';
import { Button } from './ui/button';
import { Sparkles, ArrowRight, Zap } from 'lucide-react';

const Hero = ({ scrollY }) => {
  const parallaxOffset = scrollY * 0.5;

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute top-20 left-10 w-72 h-72 bg-white/5 rounded-full blur-3xl animate-pulse"
          style={{ transform: `translateY(${parallaxOffset}px)` }}
        />
        <div
          className="absolute bottom-20 right-10 w-96 h-96 bg-white/3 rounded-full blur-3xl animate-pulse"
          style={{ transform: `translateY(-${parallaxOffset}px)`, animationDelay: '1s' }}
        />
      </div>

      {/* Grid Pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:50px_50px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,black,transparent)]"></div>

      <div className="relative z-10 max-w-6xl mx-auto px-6 text-center">
        {/* Badge */}
        <div className="inline-flex items-center space-x-2 bg-white/10 border border-white/20 rounded-full px-6 py-2 mb-8 animate-fade-in backdrop-blur-sm">
          <Sparkles className="w-4 h-4 text-white" />
          <span className="text-sm font-medium text-white">AI-Powered Development</span>
        </div>

        {/* Main Headline */}
        <h1 className="text-6xl md:text-7xl lg:text-8xl font-extrabold mb-6 leading-tight animate-fade-in-up text-white">
          Launch Your MVP in
          <span className="block mt-2 text-transparent bg-clip-text bg-gradient-to-r from-white via-gray-200 to-gray-400">
            Days — Not Months
          </span>
        </h1>

        {/* Subheading */}
        <p className="text-xl md:text-2xl text-gray-400 mb-12 max-w-4xl mx-auto leading-relaxed animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
          LaunchPilot builds investor-ready MVPs, prototypes, and AI-powered products at lightning speed.
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
          <Button
            size="lg"
            className="bg-white hover:bg-gray-100 text-black font-bold text-lg px-8 py-6 transition-all duration-300 transform hover:scale-105"
            onClick={() => window.open('https://calendly.com/rajvverma2000/30min', '_blank')}
          >
            Start Your Project
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
          <Button
            size="lg"
            variant="outline"
            className="border-white/30 text-white hover:bg-white/10 font-bold text-lg px-8 py-6 transition-all duration-300 transform hover:scale-105 backdrop-blur-sm"
            onClick={() => window.open('https://calendly.com/rajvverma2000/30min', '_blank')}
          >
            Schedule a Call
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-8 max-w-3xl mx-auto pt-8 border-t border-white/10 animate-fade-in-up" style={{ animationDelay: '0.6s' }}>
          <div>
            <div className="text-4xl font-extrabold text-white mb-2">10+</div>
            <div className="text-sm text-gray-400">Projects Completed</div>
          </div>
          <div>
            <div className="text-4xl font-extrabold text-white mb-2">3 Weeks</div>
            <div className="text-sm text-gray-400">Average MVP Time</div>
          </div>
          <div>
            <div className="text-4xl font-extrabold text-white mb-2">100%</div>
            <div className="text-sm text-gray-400">Client Satisfaction</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;